import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { usePortalAuth } from "../context/PortalAuthContext";

const inputCls =
  "w-full rounded-xl border border-[#d4cceb] bg-white px-4 py-3 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/50 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/15 transition-colors";

export default function PortalLogin() {
  const { login, isAuthenticated, user, authReady } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated || !user) return;
    if (user.role === "admin") {
      navigate("/admin", { replace: true });
    } else if (user.role === "psychologist") {
      navigate("/psicologo", { replace: true });
    } else {
      navigate("/panel", { replace: true });
    }
  }, [authReady, isAuthenticated, user, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      const role = JSON.parse(localStorage.getItem("portal_user") || "{}").role;
      toast.success("Sesión iniciada");
      if (role === "admin") {
        navigate("/admin", { replace: true });
      } else if (role === "psychologist") {
        navigate("/psicologo", { replace: true });
      } else {
        navigate("/panel", { replace: true });
      }
    } catch (err) {
      toast.error(err.message || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* ── Left — form ── */}
      <div className="flex w-full flex-col items-center bg-white min-h-screen lg:w-1/2">
        <div className="w-full max-w-md px-8 py-16 my-auto">
          {/* Header */}
          <div className="mb-10">
            <h1 className="font-sans text-[2rem] font-semibold tracking-tight text-brand-dark">
              Bienvenido de vuelta
            </h1>
            <p className="mt-2 text-[0.9375rem] text-brand-gray">
              Ingresa tus credenciales para acceder a tu cuenta.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="space-y-1.5">
              <label htmlFor="email" className="portal-label">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                placeholder="tu@correo.com"
                className={inputCls}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label htmlFor="password" className="portal-label">
                  Contraseña
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[0.8125rem] font-semibold text-brand-lavender hover:underline"
                  tabIndex={-1}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  className={`${inputCls} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-brand-gray hover:text-brand-dark transition-colors"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" aria-hidden />
                  ) : (
                    <EyeIcon className="h-5 w-5" aria-hidden />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-brand-navy py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Iniciando sesión…" : "Iniciar sesión"}
            </button>
          </form>

          <p className="mt-8 text-center text-[0.9375rem] text-brand-gray">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="font-semibold text-brand-navy hover:underline">
              Regístrate
            </Link>
          </p>
        </div>
      </div>

      {/* ── Right — brand panel ── */}
      <div
        className="hidden lg:flex flex-col items-center justify-center bg-brand-navy px-12 py-16 fixed right-0 top-0 w-1/2 h-screen"
        aria-hidden
      >
        {/* Logo in white */}
        <img
          src="/images/Logo-v2.svg"
          alt=""
          className="w-full max-w-[26rem] brightness-0 invert"
          draggable={false}
        />
        <p className="mt-8 text-[0.9375rem] font-light tracking-widest text-white/60 uppercase">
          Programa de Asistencia al Empleado
        </p>
      </div>
    </div>
  );
}
